import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  LogOut,
  Download,
  Search,
  RefreshCcw,
  Users,
  Mail,
  Phone as PhoneIcon,
  MapPin,
  Home,
  Building2,
  ChevronDown,
  Trash2,
} from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BrandHeader from "@/components/BrandHeader";

const STATUSES = ["New", "Contacted", "Closed"];

const statusStyles = {
  New: "bg-blue-100 text-blue-800",
  Contacted: "bg-orange-100 text-orange-800",
  Closed: "bg-green-100 text-green-800",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        api.get("/leads"),
        api.get("/leads/stats"),
      ]);
      setLeads(leadsRes.data || []);
      setStats(statsRes.data || {});
    } catch (e) {
      // 401 redirect handled by interceptor
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user !== false && user !== null) fetchAll();
  }, [user]);

  if (user === null) {
    return (
      <div
        className="min-h-screen bg-[#F8FAFC] flex items-center justify-center text-[#001B33] font-semibold"
        style={{ fontFamily: "Montserrat, sans-serif" }}
      >
        Loading…
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;

  const filtered = (() => {
    let list = leads;
    if (statusFilter !== "All") list = list.filter((l) => l.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((l) =>
        [l.name, l.email, l.phone, l.address, l.zip_code]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }
    return list;
  })();

  const updateStatus = async (id, status) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await api.patch(`/leads/${id}/status`, { status });
      fetchAll();
    } catch {
      fetchAll();
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/leads/${id}`);
      fetchAll();
    } catch (_e) {
      /* noop */
    }
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("sis_token");
    const res = await fetch(`${API}/leads/export`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `solar-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div
      className="min-h-screen bg-[#F8FAFC]"
      style={{ fontFamily: "Montserrat, sans-serif" }}
      data-testid="admin-dashboard-root"
    >
      <BrandHeader
        right={
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-[#FF8800] font-semibold transition-colors"
          >
            <LogOut className="h-4 w-4" /> Logout
          </button>
        }
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-[#FF8800]">
              Welcome back
            </p>
            <h1 className="mt-1 text-3xl md:text-4xl font-semibold tracking-tight text-[#001B33]">
              Lead Dashboard
            </h1>
            <div className="mt-2 h-1 w-16 bg-[#FF8800] rounded-full" />
          </div>
          <div className="flex gap-2">
            <button
              data-testid="admin-refresh-btn"
              onClick={fetchAll}
              className="inline-flex items-center gap-2 px-4 py-2.5 border-2 border-slate-200 hover:border-[#001B33] rounded-lg text-sm font-semibold text-[#001B33] transition-colors"
            >
              <RefreshCcw className="h-4 w-4" /> Refresh
            </button>
            <button
              data-testid="admin-export-csv-btn"
              onClick={exportCsv}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#001B33] hover:bg-[#002a52] text-white rounded-lg text-sm font-semibold transition-colors"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Leads" value={stats.total} testid="stat-total" accent="#001B33" />
          <StatCard label="New" value={stats.new} testid="stat-new" accent="#2563eb" />
          <StatCard label="Contacted" value={stats.contacted} testid="stat-contacted" accent="#FF8800" />
          <StatCard label="Closed" value={stats.closed} testid="stat-closed" accent="#16a34a" />
        </div>

        {/* Filters */}
        <div className="mt-6 bg-white rounded-xl border border-slate-200 p-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              data-testid="admin-search-input"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, phone, address…"
              className="w-full pl-10 pr-3 py-2.5 border-2 border-slate-200 rounded-lg text-sm font-medium focus:border-[#FF8800] focus:ring-1 focus:ring-[#FF8800] outline-none transition-colors"
            />
          </div>
          <div className="flex gap-2">
            {["All", ...STATUSES].map((s) => (
              <button
                key={s}
                data-testid={`admin-filter-${s.toLowerCase()}-btn`}
                onClick={() => setStatusFilter(s)}
                className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                  statusFilter === s
                    ? "bg-[#001B33] text-white"
                    : "bg-slate-100 text-[#001B33] hover:bg-slate-200"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="mt-6 bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="admin-leads-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <Th>Date</Th>
                  <Th>Type</Th>
                  <Th>Name</Th>
                  <Th>Contact</Th>
                  <Th>Address</Th>
                  <Th>Bill {">"} $100</Th>
                  <Th>Status</Th>
                  <Th>{""}</Th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="p-10 text-center text-slate-500 font-medium">
                      Loading leads…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="p-12 text-center"
                      data-testid="admin-empty-state"
                    >
                      <Users className="h-10 w-10 text-slate-300 mx-auto" />
                      <p className="mt-3 text-[#001B33] font-semibold">No leads yet</p>
                      <p className="mt-1 text-slate-500 text-sm">
                        New quiz submissions will appear here.
                      </p>
                    </td>
                  </tr>
                ) : (
                  filtered.map((l) => (
                    <tr
                      key={l.id}
                      data-testid={`lead-row-${l.id}`}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      <td className="p-4 align-top text-slate-600 font-medium whitespace-nowrap">
                        {formatDate(l.created_at)}
                      </td>
                      <td className="p-4 align-top">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                            l.owner_type === "business"
                              ? "bg-[#001B33] text-white"
                              : "bg-slate-100 text-[#001B33]"
                          }`}
                        >
                          {l.owner_type === "business" ? (
                            <Building2 className="h-3 w-3" />
                          ) : (
                            <Home className="h-3 w-3" />
                          )}
                          {l.owner_type === "business" ? "Business" : "Home"}
                        </span>
                      </td>
                      <td className="p-4 align-top text-[#001B33] font-semibold">
                        {l.name}
                      </td>
                      <td className="p-4 align-top">
                        <div className="flex items-center gap-1.5 text-[#001B33] font-medium">
                          <PhoneIcon className="h-3.5 w-3.5 text-slate-400" />
                          {l.phone}
                        </div>
                        {l.email && (
                          <div className="mt-1 flex items-center gap-1.5 text-slate-600 text-xs">
                            <Mail className="h-3.5 w-3.5 text-slate-400" />
                            {l.email}
                          </div>
                        )}
                      </td>
                      <td className="p-4 align-top text-[#001B33] max-w-xs">
                        <div className="flex items-start gap-1.5 font-medium">
                          <MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-2">{l.address}</span>
                        </div>
                        <div className="mt-1 text-xs text-slate-500">Zip: {l.zip_code}</div>
                      </td>
                      <td className="p-4 align-top">
                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                            l.bill_over_100
                              ? "bg-orange-100 text-orange-800"
                              : "bg-slate-100 text-slate-700"
                          }`}
                        >
                          {l.bill_over_100 ? "Yes" : "No"}
                        </span>
                      </td>
                      <td className="p-4 align-top">
                        <div className="relative inline-block">
                          <select
                            data-testid={`lead-status-select-${l.id}`}
                            value={l.status}
                            onChange={(e) => updateStatus(l.id, e.target.value)}
                            className={`appearance-none pr-7 pl-3 py-1 rounded-full text-xs font-semibold border border-transparent focus:outline-none focus:ring-2 focus:ring-[#FF8800] ${statusStyles[l.status]}`}
                          >
                            {STATUSES.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="h-3 w-3 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none opacity-60" />
                        </div>
                      </td>
                      <td className="p-4 align-top">
                        <button
                          data-testid={`lead-delete-btn-${l.id}`}
                          onClick={() => deleteLead(l.id)}
                          className="text-slate-400 hover:text-red-600 transition-colors"
                          title="Delete lead"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-slate-500 font-medium">
          Logged in as <span className="text-[#001B33] font-semibold">{user.email}</span>
        </p>
      </main>
    </div>
  );
}

function StatCard({ label, value, testid, accent }) {
  return (
    <div
      data-testid={testid}
      className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow"
    >
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div
        className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight"
        style={{ color: accent }}
      >
        {value ?? 0}
      </div>
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="text-left text-xs font-semibold text-[#001B33] uppercase tracking-wider p-4">
      {children}
    </th>
  );
}

function formatDate(d) {
  if (!d) return "—";
  try {
    const date = new Date(d);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return String(d);
  }
}
